import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../../services/financeService';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { toPaisa } from '../../../../utils/paisa';

type Kind = 'charge' | 'profit';

const KINDS: Record<Kind, { label: string; hint: string; placeholder: string }> = {
  charge: { label: 'Bank charge', hint: 'Posts to Bank & Gateway Charges and comes out of the bank.', placeholder: 'e.g. SMS alert fee, cheque book' },
  profit: { label: 'Profit received', hint: 'Posts to Profit on Bank Deposits and goes into the bank.', placeholder: 'e.g. Profit for September' },
};

/**
 * A line seen only on the statement — a charge or profit the bank applied.
 * It posts to the books and arrives in the reconciliation already ticked.
 */
const BankAdjustmentDialog: React.FC<{
  open: boolean;
  reconciliationId: string;
  statementDate: string;
  onClose: () => void;
  onPosted: () => void;
}> = ({ open, reconciliationId, statementDate, onClose, onPosted }) => {
  const { currency } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { formatDate } = useTimezone();
  const [kind, setKind] = useState<Kind>('charge');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind('charge');
    setAmount('');
    setDate(statementDate);
    setNote('');
    setError(null);
  }, [open, statementDate]);

  const paisa = toPaisa(amount);
  const late = !!date && date > statementDate;
  const canSubmit = !busy && !!paisa && !!date && !late && !!note.trim();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const entry = await financeService.addBankAdjustment(reconciliationId, { kind, amountPaisa: paisa!, date, note: note.trim() });
      enqueueSnackbar(`Posted as ${entry.entryNo} and ticked`, { variant: 'success' });
      onPosted();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'It could not be posted.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Add from the statement
        <Typography variant="body2" color="text.secondary">For lines the bank applied that aren't in the books yet.</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={kind}
            onChange={(_, v) => v && setKind(v)}
            disabled={busy}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
          >
            {(Object.keys(KINDS) as Kind[]).map(k => <ToggleButton key={k} value={k}>{KINDS[k].label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: '8px !important' }}>{KINDS[kind].hint}</Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Amount"
              size="small"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              error={amount.trim() !== '' && !paisa}
              disabled={busy}
              autoFocus
              sx={{ flex: 1 }}
            />
            <TextField
              label="Date"
              type="date"
              size="small"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: statementDate }}
              error={late}
              disabled={busy}
              sx={{ width: 170 }}
            />
          </Stack>
          {late && <Typography variant="caption" color="error">The date must be on or before {formatDate(statementDate)}.</Typography>}
          <TextField
            label="As it appears on the statement"
            size="small"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={KINDS[kind].placeholder}
            inputProps={{ maxLength: 200 }}
            disabled={busy}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!canSubmit}>{busy ? 'Posting…' : 'Post and tick'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankAdjustmentDialog;
